import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import axios from '../../lib/axios'
import { useOnboarding } from '../../context/OnboardingContext'
import {
  Plus,
  Play,
  Pause,
  RotateCcw,
  Trash2,
  BarChart3,
  CheckCircle,
  Clock,
  AlertCircle,
  RefreshCw,
  Megaphone,
  Search,
  Layers,
  Zap,
  Target,
  Sparkles,
  AlertTriangle
} from 'lucide-react'
import '../Pages.css'

export default function Campaigns() {
  const [campaigns, setCampaigns] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [actionLoading, setActionLoading] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [metrics, setMetrics] = useState({}) // { [id]: {sent, open, click} | null }

  const { state: onb } = useOnboarding()
  const gateOpen = onb.steps.mailbox && onb.steps.dns
  const gateReason = !onb.steps.mailbox
    ? 'Connect a mailbox first.'
    : !onb.steps.dns ? 'Verify your DNS first.' : null
  const gateStyle = gateOpen ? {} : { pointerEvents: 'none', opacity: 0.45, filter: 'grayscale(.4)' }
  const onGateClick = (e) => { if (!gateOpen) { e.preventDefault(); alert(gateReason) } }

  useEffect(() => {
    fetchCampaigns()

    // Refresh when user navigates back to this tab/page
    const handleFocus = () => fetchCampaigns()
    window.addEventListener('focus', handleFocus)

    // Also poll every 30s so paused stat card stays current
    const interval = setInterval(fetchCampaigns, 30000)

    return () => {
      window.removeEventListener('focus', handleFocus)
      clearInterval(interval)
    }
  }, [])

  const fetchCampaigns = () => {
    setLoading(true)
    axios
      .get('email-campaign/get-all')
      .then((res) => {
        setCampaigns(res.data)
        setError(null)
        fetchMetrics(res.data)
      })
      .catch((err) => {
        console.error(err)
        setError('Failed to load campaigns')
      })
      .finally(() => {
        setLoading(false)
      })
  }

  // Pull per-campaign KPI strip (Sent / Open% / Click%) in parallel.
  // Drafts are all-zero — skip the request. Failures degrade to "—".
  const fetchMetrics = (list) => {
    list.forEach((c) => {
      if (c.status === 'draft') return
      axios
        .get(`email-campaign/analytics/${c.id}`)
        .then((res) => {
          const a = res.data || {}
          setMetrics((m) => ({
            ...m,
            [c.id]: { sent: a.total_sent || 0, open: a.open_rate || 0, click: a.click_rate || 0 },
          }))
        })
        .catch(() => setMetrics((m) => ({ ...m, [c.id]: null })))
    })
  }

  const handleStart = async (campaignId) => {
    setActionLoading(campaignId)
    try {
      await axios.post(`email-campaign/${campaignId}/start`)
      fetchCampaigns()
    } catch (err) {
      const detail = err.response?.data?.detail
      // Backend returns {errors: [...]} for pre-launch validation failures
      if (detail && typeof detail === 'object' && Array.isArray(detail.errors)) {
        alert('Cannot launch campaign:\n\n' + detail.errors.map(e => `• ${e}`).join('\n'))
      } else {
        alert(typeof detail === 'string' ? detail : 'Failed to start campaign')
      }
    } finally {
      setActionLoading(null)
    }
  }

  const handlePause = async (campaignId) => {
    setActionLoading(campaignId)
    try {
      await axios.post(`email-campaign/${campaignId}/pause`)
      fetchCampaigns()
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to pause campaign')
    } finally {
      setActionLoading(null)
    }
  }

  const handleResume = async (campaignId) => {
    setActionLoading(campaignId)
    try {
      await axios.post(`email-campaign/${campaignId}/resume`)
      fetchCampaigns()
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to resume campaign')
    } finally {
      setActionLoading(null)
    }
  }

  const handleDelete = async (campaignId) => {
    if (!confirm('Are you sure you want to delete this campaign?')) return

    setActionLoading(campaignId)
    try {
      await axios.delete(`email-campaign/${campaignId}`)
      fetchCampaigns()
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete campaign')
    } finally {
      setActionLoading(null)
    }
  }

  const statusConfig = {
    draft: {
      label: 'Draft',
      bg: 'rgba(100, 116, 139, 0.1)',
      color: '#64748b',
      accent: '#64748b',
      icon: Clock,
      pulse: false
    },
    active: {
      label: 'Active',
      bg: 'rgba(34, 197, 94, 0.1)',
      color: '#22c55e',
      accent: '#22c55e',
      icon: Play,
      pulse: true
    },
    paused: {
      label: 'Paused',
      bg: 'rgba(234, 179, 8, 0.1)',
      color: '#eab308',
      accent: '#eab308',
      icon: Pause,
      pulse: false
    },
    completed: {
      label: 'Completed',
      bg: 'rgba(99, 102, 241, 0.1)',
      color: '#6366f1',
      accent: '#6366f1',
      icon: CheckCircle,
      pulse: false
    }
  }

  const getStatusBadge = (campaign) => {
    const isAutoPaused = campaign.status === 'paused' && campaign.pause_reason
    const config = isAutoPaused
      ? {
          label: 'Auto-Paused',
          bg: 'rgba(239, 68, 68, 0.1)',
          color: '#ef4444',
          icon: AlertTriangle,
          pulse: false
        }
      : statusConfig[campaign.status] || statusConfig.draft
    return (
      <span className="campaign-status-badge" style={{ background: config.bg, color: config.color }}>
        <span className={`campaign-pulse-dot${config.pulse ? ' animate' : ''}`} />
        {config.label}
      </span>
    )
  }

  const getActionButton = (campaign) => {
    const isLoading = actionLoading === campaign.id

    if (isLoading) {
      return (
        <button className="btn btn-sm" disabled style={{ minWidth: '90px' }}>
          <RefreshCw size={14} className="animate-spin" />
        </button>
      )
    }

    switch (campaign.status) {
      case 'draft':
        return (
          <button
            className="btn btn-sm"
            onClick={() => handleStart(campaign.id)}
            style={{
              background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
              color: 'white',
              border: 'none'
            }}
          >
            <Play size={13} />
            <span>Start</span>
          </button>
        )
      case 'active':
        return (
          <button
            className="btn btn-sm"
            onClick={() => handlePause(campaign.id)}
            style={{
              background: 'rgba(234, 179, 8, 0.1)',
              color: '#ca8a04',
              border: '1px solid rgba(234, 179, 8, 0.3)'
            }}
          >
            <Pause size={13} />
            <span>Pause</span>
          </button>
        )
      case 'paused':
        return (
          <button
            className="btn btn-sm"
            onClick={() => handleResume(campaign.id)}
            style={{
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              color: 'white',
              border: 'none'
            }}
          >
            <RotateCcw size={13} />
            <span>Resume</span>
          </button>
        )
      case 'completed':
        return (
          <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.35rem',
            fontSize: '0.8rem',
            color: 'var(--color-text-muted)'
          }}>
            <CheckCircle size={13} />
            Finished
          </span>
        )
      default:
        return null
    }
  }

  // Stats
  const stats = {
    total: campaigns.length,
    active: campaigns.filter(c => c.status === 'active').length,
    paused: campaigns.filter(c => c.status === 'paused').length,
    completed: campaigns.filter(c => c.status === 'completed').length
  }

  // Filtered campaigns
  const filteredCampaigns = campaigns.filter(c => {
    const matchesSearch = !searchQuery || c.name.toLowerCase().includes(searchQuery.toLowerCase())
    const matchesStatus = statusFilter === 'all' || c.status === statusFilter
    return matchesSearch && matchesStatus
  })

  const statCards = [
    { label: 'Total', value: stats.total, gradient: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)', icon: Layers },
    { label: 'Active', value: stats.active, gradient: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)', icon: Zap },
    { label: 'Paused', value: stats.paused, gradient: 'linear-gradient(135deg, #eab308 0%, #ca8a04 100%)', icon: Pause },
    { label: 'Completed', value: stats.completed, gradient: 'linear-gradient(135deg, #6366f1 0%, #a855f7 100%)', icon: Target }
  ]

  const filterOptions = [
    { key: 'all', label: 'All' },
    { key: 'active', label: 'Active' },
    { key: 'paused', label: 'Paused' },
    { key: 'draft', label: 'Draft' },
    { key: 'completed', label: 'Completed' }
  ]

  return (
    <div className="page campaigns-page animate-fade-in">
      <div className="page-header">
        <div className="header-info">
          <h2>Campaigns</h2>
          <p>Manage your email campaigns and track their performance</p>
        </div>
        <div className="campaign-header-actions" title={gateReason || ''}>
          <Link to="/campaigns/ai-create" onClick={onGateClick} className="btn btn-ai"
                style={{ boxShadow: '0 4px 12px rgba(168, 85, 247, 0.3)', ...gateStyle }}>
            <Sparkles size={18} />
            <span>Create with AI</span>
          </Link>
          <Link to="/campaigns/add" onClick={onGateClick} className="btn btn-primary" style={{
            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
            boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
            ...gateStyle,
          }}>
            <Plus size={18} />
            <span>Add Campaign</span>
          </Link>
        </div>
      </div>

      {error && (
        <div className="card" style={{
          padding: '1.5rem',
          background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.05) 0%, rgba(220, 38, 38, 0.05) 100%)',
          border: '1px solid rgba(239, 68, 68, 0.2)',
          marginBottom: '1.5rem'
        }}>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
            <AlertCircle size={20} style={{ color: '#ef4444' }} />
            <span style={{ color: '#dc2626', flex: 1 }}>{error}</span>
            <button onClick={fetchCampaigns} className="btn btn-primary" style={{ padding: '0.5rem 1rem' }}>
              <RefreshCw size={16} />
              <span>Retry</span>
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px'
        }}>
          <div className="loader"></div>
        </div>
      ) : campaigns.length === 0 ? (
        /* Empty State - Side by Side */
        <div className="card" style={{
          background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.03) 0%, rgba(139, 92, 246, 0.03) 100%)',
          border: '1px solid rgba(99, 102, 241, 0.1)'
        }}>
          <div className="campaign-empty-state">
            <div className="campaign-empty-illustration">
              <Megaphone size={80} style={{ color: '#6366f1' }} />
            </div>
            <div className="campaign-empty-content">
              <h3>Launch Your First Campaign</h3>
              <p>
                Create personalized email campaigns to reach your audience.
                Set up sequences, track engagement, and optimize your outreach — all in one place.
              </p>
              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', justifyContent: 'center' }} title={gateReason || ''}>
                <Link to="/campaigns/ai-create" onClick={onGateClick} className="btn btn-ai" style={{
                  padding: '0.875rem 2rem',
                  fontSize: '1rem',
                  boxShadow: '0 4px 12px rgba(168, 85, 247, 0.3)',
                  ...gateStyle,
                }}>
                  <Sparkles size={20} />
                  <span>Create with AI</span>
                </Link>
                <Link to="/campaigns/add" onClick={onGateClick} className="btn btn-primary" style={{
                  padding: '0.875rem 2rem',
                  fontSize: '1rem',
                  background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                  boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
                  ...gateStyle,
                }}>
                  <Plus size={20} />
                  <span>Create Manually</span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* Stat Cards */}
          <div className="campaign-stats-grid">
            {statCards.map((s) => {
              const Icon = s.icon
              return (
                <div key={s.label} className="campaign-stat-card">
                  <div className="campaign-stat-icon" style={{ background: s.gradient }}>
                    <Icon size={22} style={{ color: 'white' }} />
                  </div>
                  <div>
                    <span className="campaign-stat-value">{s.value}</span>
                    <span className="campaign-stat-label">{s.label}</span>
                  </div>
                </div>
              )
            })}
          </div>

          {/* Search & Filter */}
          <div className="campaign-search-bar">
            <div className="campaign-search-input">
              <Search size={16} className="search-icon" />
              <input
                type="text"
                placeholder="Search campaigns..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <div className="campaign-filter-chips">
              {filterOptions.map((f) => (
                <button
                  key={f.key}
                  className={`campaign-filter-chip${statusFilter === f.key ? ' active' : ''}`}
                  onClick={() => setStatusFilter(f.key)}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          {/* Campaign Grid */}
          {filteredCampaigns.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '3rem',
              color: 'var(--color-text-muted)',
              fontSize: '0.9rem'
            }}>
              No campaigns match your filters.
            </div>
          ) : (
            <div className="campaign-rich-grid">
              {filteredCampaigns.map((campaign) => {
                const config = statusConfig[campaign.status] || statusConfig.draft
                const hasAlert = campaign.status === 'paused' && !!campaign.pause_reason
                const StatusIcon = hasAlert ? AlertTriangle : config.icon
                const iconBg = hasAlert ? 'rgba(239, 68, 68, 0.14)' : config.bg
                const iconColor = hasAlert ? '#ef4444' : config.color
                const tint = hasAlert ? 'rgba(239, 68, 68, 0.12)' : config.bg
                const m = campaign.status === 'draft' ? { sent: 0, open: 0, click: 0 } : metrics[campaign.id]
                const when = campaign.status === 'completed'
                  ? 'Finished'
                  : campaign.started_at ? `Started ${campaign.started_at}`
                  : campaign.created_at ? `Created ${campaign.created_at}`
                  : 'Not started yet'
                return (
                  <div
                    key={campaign.id}
                    className={`campaign-rich-card${hasAlert ? ' has-alert' : ''}`}
                    style={{ background: `radial-gradient(130% 120% at 100% 0%, ${tint}, transparent 55%), var(--color-bg-card)` }}
                  >
                    <div className="crc-top">
                      <div className="crc-id">
                        <div className="crc-ic" style={{ background: iconBg, color: iconColor }}>
                          <StatusIcon size={20} />
                        </div>
                        <div className="crc-headtext">
                          <h3>{campaign.name}</h3>
                          <span className="crc-sub">{campaign.description || when}</span>
                        </div>
                      </div>
                      <div className="crc-top-right">
                        {hasAlert && (
                          <span className="crc-alert" tabIndex={0} aria-label={campaign.pause_reason}>
                            <AlertTriangle size={15} />
                            <span className="crc-tip">{campaign.pause_reason}</span>
                          </span>
                        )}
                        {getStatusBadge(campaign)}
                      </div>
                    </div>

                    <div className="crc-metrics">
                      <div className="crc-metric">
                        <b>{m ? m.sent.toLocaleString() : '—'}</b>
                        <span>Sent</span>
                      </div>
                      <div className="crc-metric">
                        <b>{m ? `${m.open}%` : '—'}</b>
                        <span>Open</span>
                      </div>
                      <div className="crc-metric">
                        <b>{m ? `${m.click}%` : '—'}</b>
                        <span>Click</span>
                      </div>
                    </div>

                    <div className="crc-foot">
                      <Link to={`/campaigns/analytics/${campaign.id}`} className="btn btn-sm crc-manage">
                        <BarChart3 size={14} />
                        <span>Manage</span>
                      </Link>
                      {getActionButton(campaign)}
                      <div className="spacer" />
                      <button
                        className="btn btn-sm crc-del"
                        onClick={() => handleDelete(campaign.id)}
                        disabled={actionLoading === campaign.id}
                        title="Delete campaign"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </>
      )}
    </div>
  )
}
